import type { ReactNode } from 'react';

import type { TotalProps } from '#pagination/components/types/total.props';

import { usePaginationSelector } from '#pagination/context/hooks/selector.hook';
import { useTotalPages } from '#pagination/hooks/total-pages.hook';

/**
 * Custom hook that computes and formats the display string for pagination totals,
 * based on the provided `totalType`. It selects pagination state values (page, pageSize, total)
 * from context and memoizes the computed output for performance.
 *
 * @param params - An object containing the `totalType` which determines the display mode.
 * @param params.totalType - The type of total to display: 'pageCount', 'recordCount', or 'totalCount'.
 *
 * @returns The formatted total string according to the selected `totalType`.
 *
 * - For 'pageCount': Displays the current page and total number of pages.
 * - For 'recordCount': Displays the range of records shown on the current page and the total number of records.
 * - For 'totalCount': Displays the total number of rows.
 *
 * @example
 * const totalString = useTotalConfiguration({ totalType: 'recordCount' });
 */
export const useTotalConfiguration = ({ totalType }: Pick<TotalProps, 'totalType'>) => {
  const page = usePaginationSelector(({ page }) => {
    return page;
  });
  const pageSize = usePaginationSelector(({ pageSize }) => {
    return pageSize;
  });
  const total = usePaginationSelector(({ total }) => {
    return total;
  });

  const totalPages = useTotalPages();
  let Component: ReactNode | null = null;

  switch (totalType) {
    case 'pageCount':
      Component = (
        <>
          Show Page<span className="font-bold">{page}</span> of <span className="font-bold">{totalPages}</span>
        </>
      );
      break;

    case 'recordCount':
      Component = (
        <>
          Showing
          <span className="font-bold">
            {total === 0 ? '0' : `${(page - 1) * pageSize + 1} - ${Math.min(page * pageSize, total)}`}
          </span>
          of
          <span className="font-bold">{total}</span>
          items
        </>
      );
      break;

    case 'totalCount':
      Component = (
        <>
          Total Rows:<span className="font-bold">{total}</span>
        </>
      );
      break;
  }

  return Component;
};
